import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import React from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import MenuItem from '../ui-components/MenuItem';
import { TypedMap } from 'flow/types/TypedMap';
import { ChatSession } from 'flow/types/Chats';
import { unMuteSession, muteSession } from 'flow/actions/chat-actions';
import NestedMenuItem from '../ui-components/NestedMenuItem';
import { getIsSinglePaneMode } from 'flow/selectors/selectors';
import { extractStatusText } from 'flow/utils';
import {
    selectExtensionActiveProfile,
    selectExtensionActiveProfileActiveUntil,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';

interface Props {
    session: TypedMap<ChatSession>;
}

interface MuteDurationOptions {
    label: string;
    duration: number | null;
}

const MuteSessionMenuItem = ({ session }: Props) => {
    const { i18next } = useAppContext();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const dispatch = useAppDispatch();
    const activeUntil = useAppSelector((state) =>
        selectExtensionActiveProfileActiveUntil(state, ownExtensionKey),
    );
    const activeUntilTimestamp = activeUntil
        ? new Date(activeUntil).getTime()
        : null;
    const activeProfile = useAppSelector((state) =>
        selectExtensionActiveProfile(state, ownExtensionKey),
    );

    const statusText = extractStatusText(activeProfile, i18next);

    const isSinglePaneMode = useAppSelector(getIsSinglePaneMode);

    const quickOptions: MuteDurationOptions[] = [
        { label: 'datetime.hours', duration: 1 },
        { label: 'datetime.hours', duration: 8 },
        { label: 'datetime.hours', duration: 24 },
        {
            label: i18next.t('profilechooser.until_further_notice'),
            duration: null,
        },
    ];

    const handleMuteUntil = (until: number | null) => {
        dispatch(muteSession(session.get('key'), until));
    };

    const handleMute = (duration: number | null) => {
        handleMuteUntil(
            duration ? new Date().getTime() + duration * 60 * 60 * 1000 : null,
        );
    };

    return session.get('chatMuted') ? (
        <MenuItem
            onClick={(e) => {
                e.stopPropagation();
                dispatch(unMuteSession(session.get('key')));
            }}
        >
            {i18next.t('Chat.Mute.Inactivate')}
        </MenuItem>
    ) : (
        <NestedMenuItem
            title={i18next.t('Chat.Mute.Activate')}
            placementDesired={isSinglePaneMode ? 'left-start' : 'right'}
            className={isSinglePaneMode ? 'min-w-[150px]' : null}
        >
            {quickOptions.map((option, i) => (
                <MenuItem
                    key={option.duration}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleMute(option.duration);
                    }}
                >
                    {i18next.t(option.label, {
                        count: option.duration,
                    })}
                </MenuItem>
            ))}
            {activeUntilTimestamp ? (
                <MenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        handleMuteUntil(activeUntilTimestamp);
                    }}
                >
                    {i18next.t('Chat.Mute.UntilProfile', {
                        ProfileStatus: statusText,
                    })}
                </MenuItem>
            ) : null}
        </NestedMenuItem>
    );
};

export default MuteSessionMenuItem;
